import { CircularProgressWithLabel } from '@app/components/base/CircularProgressWithLabel';
import { ConfirmationAction } from '@app/components/ConfirmationAction';
import CheckIcon from '@mui/icons-material/Check';
import MemoryIcon from '@mui/icons-material/Memory';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { OrderReprocessButtonLabel } from './OrderReprocessButtonLabel';
import { useOrderReprocess } from './useOrderReprocess';

interface Props {
  orders: string[];
  reprocessKey?: string;
  isDisabled?: boolean;
  onReprocessComplete?: () => void;
  onReprocessError?: (error: Error) => void;
}

/**
 * Confirmation button that starts an order reprocess and reflects the job's pending/completed state.
 */
export function OrderReprocessAction({
  orders,
  reprocessKey,
  isDisabled = false,
  onReprocessComplete,
  onReprocessError,
}: Props) {
  const { $t } = useIntl();

  const { reprocessMutation, statusQuery, currentOrders, totalOrders } = useOrderReprocess({
    orders,
    reprocessKey,
    onReprocessComplete,
    onReprocessError,
  });

  const progressPercent = statusQuery.data?.progressPercent;
  const isPending = Boolean(statusQuery.data?.isPending);
  const isCompleted = statusQuery.data?.status === 'COMPLETED';

  return (
    <ConfirmationAction
      title={$t({
        id: 'audit.aod.actions.orderReprocess.confirm.title',
        defaultMessage: 'Confirmar reprocesamiento',
      })}
      message={
        <Typography variant="body1">
          {reprocessKey
            ? $t(
                {
                  id: 'audit.aod.actions.orderReprocess.confirm.message.withKey',
                  defaultMessage:
                    '¿Está seguro de que desea iniciar el reprocesamiento de la orden {orderId}? (key: {key})',
                },
                {
                  orderId: orders.join(', '),
                  key: reprocessKey,
                },
              )
            : $t(
                {
                  id: 'audit.aod.actions.orderReprocess.confirm.message',
                  defaultMessage:
                    '¿Está seguro de que desea iniciar el reprocesamiento de la orden {orderId}?',
                },
                { orderId: orders.join(', ') },
              )}
        </Typography>
      }
      confirmLabel={$t({
        id: 'audit.aod.actions.orderReprocess.confirm.button',
        defaultMessage: 'Confirmar',
      })}
      onConfirm={async () => {
        reprocessMutation.mutateAsync({
          data: {
            orderId: currentOrders,
            ...(reprocessKey && { reprocessKey }),
          },
        });
      }}
      onError={onReprocessError}
    >
      {({ onClick }) => {
        const button = (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            disabled={isPending || isCompleted || isDisabled}
            startIcon={<MemoryIcon />}
            endIcon={
              isPending ? (
                <Box sx={{ ml: 1 }}>
                  <CircularProgressWithLabel
                    labelPosition="left"
                    value={progressPercent ?? 100}
                    size={12}
                  />
                </Box>
              ) : isCompleted ? (
                <CheckIcon />
              ) : null
            }
            onClick={onClick}
          >
            <OrderReprocessButtonLabel
              currentOrders={currentOrders}
              totalOrders={totalOrders}
              isPending={isPending}
              isCompleted={isCompleted}
            />
          </Button>
        );

        return isCompleted && statusQuery.data?.finishedOn ? (
          <Tooltip
            placement="left"
            title={
              reprocessKey
                ? $t(
                    {
                      id: 'audit.aod.actions.orderReprocess.reprocessedOn.withKey',
                      defaultMessage:
                        'Reprocesado el {date, date, long} {date, time, short} (key: {key})',
                    },
                    {
                      date: new Date(statusQuery.data.finishedOn),
                      key: reprocessKey,
                    },
                  )
                : $t(
                    {
                      id: 'audit.aod.actions.orderReprocess.reprocessedOn',
                      defaultMessage: 'Reprocesado el {date, date, long} {date, time, short}',
                    },
                    { date: new Date(statusQuery.data.finishedOn) },
                  )
            }
          >
            <Box component="span" sx={{ display: 'inline-block' }}>
              {button}
            </Box>
          </Tooltip>
        ) : (
          button
        );
      }}
    </ConfirmationAction>
  );
}
